'use client';

import { ResponsiveModal } from '@/components/ui/ResponsiveModal';
import { AuthFlowContent } from '@/features/auth/AuthFlowContent';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useAuthModal } from '@/hooks/useAuthModal';
import { useStore } from '@/providers/StoreProvider';
import { isProfileComplete } from '@repo/utils';
import { useIsMobile } from '@/hooks/useDeviceType';

export function AuthSheet() {
  const isOpen = useStore(s => s.authUI.isOpen);
  const locked = useStore(s => s.authUI.locked);
  const config = useStore(s => s.authUI.config);
  const step = useStore(s => s.authUI.step);
  const close = useStore(s => s.authUI.close);
  const setStep = useStore(s => s.authUI.setStep);
  const setProfileCompletionPromptMutedUntil = useStore(
    s => s.authUI.setProfileCompletionPromptMutedUntil,
  );
  const profile = useStore(s => s.profile.profile);
  const analytics = useAnalytics();
  const { resolveAuthFlow } = useAuthModal();
  const isMobile = useIsMobile();

  // Provide onBack handler based on current step
  const handleBack = (() => {
    if (locked) return undefined;
    switch (step) {
      case 'verify-phone':
        return () => setStep('signup-details');
      case 'login-otp':
        return () => {
          analytics.track('login_signup_backbutton');
          setStep('email-input');
        };
      // Add more cases here for other steps that need back navigation
      default:
        return undefined;
    }
  })();

  // Provide onClose handler based on current step
  // Returns undefined to hide close button for specific steps
  const handleClose = (() => {
    switch (step) {
      case 'signup-details':
        return undefined; // Hide close button for signup-details step
      case 'verify-phone':
        return () => {
          if (!isProfileComplete(profile)) {
            const mutedUntil = Date.now() + 24 * 60 * 60 * 1000;
            setProfileCompletionPromptMutedUntil(mutedUntil);
            window.localStorage?.setItem('authUIModalMutedUntil', JSON.stringify(mutedUntil));
            analytics.track('login_signup_close');
          }
          resolveAuthFlow();
        };
      default:
        return () => {
          if (!locked) {
            analytics.track('login_signup_close');
            close();
          }
        };
    }
  })();

  // Disable drag down for update profile step (signup-details) on mobile
  const isUpdateProfileStep = step === 'signup-details';
  const disableDrag = isUpdateProfileStep;
  // Only disable dismissing on mobile for update profile step
  const shouldDisableDismissing = isMobile && isUpdateProfileStep;

  return (
    <ResponsiveModal
      open={isOpen}
      {...(handleClose !== undefined && { onClose: handleClose })}
      label={config.title ?? ''}
      maxWidth="md"
      closeOnOverlayClick={step === 'verify-phone' ? false : true}
      dismissible={!locked && !shouldDisableDismissing}
      mobileFullHeight={true}
      {...(handleBack !== undefined && { onBack: handleBack })}
      showHandle={!disableDrag}
      handleOnly={true}
    >
      <AuthFlowContent />
    </ResponsiveModal>
  );
}
